// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import { width as ssWidth } from 'styled-system';
import Box from 'components/Box';
import { colors, fontWeights } from '../theme';

const hexToDecimalArray = (color: string) => {
  const digits = color.replace('#', '');
  return new Array(3)
    .fill(0)
    .map((item, index) => parseInt(digits.substr(index * 2, 2), 16));
};

const decimalArrayToHex = (color: Array<number>) => {
  const hexString = color.map(group => Math.round(group).toString(16)).join('');
  return `#${hexString}`;
};

export const DefaultButton = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  box-shadow: 0 0 0 2px currentColor inset;
  border-radius: 50%;
  color: ${colors.waterBlue};
  font-size: 10px;
  font-weight: ${fontWeights.semiBold};
  transition: all 0.3s ease;
  &::after {
    content: '?';
  }
`;

export const IconButton = styled(Box)`
  color: ${colors.waterBlue};
`;

const TooltipBox = styled.div`
  position: absolute;
  transform: translate3d(0, -100%, 0);
  top: -20px;
  right: ${props => props.right}px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  padding: 20px 20px 27px;
  width: 300px;
  background-color: white;
  background-image: linear-gradient(
    to left,
    ${colors.skyBlue},
    ${colors.niceBlue}
  );
  background-size: 100% 7px;
  background-repeat: no-repeat;
  background-position: left bottom;
  box-shadow: 0 5px 12px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  color: ${colors.battleshipGrey};
  font-size: 14px;
  text-align: left;
  ${ssWidth}
`;

const Button = styled(
  React.forwardRef(
    (
      {
        component: Component,
        triangleColor,
        ...props
      }: { component: React.ElementType, triangleColor: string },
      ref
    ) => <Component ref={ref} {...props} />
  )
)`
  position: relative;
  display: block;
  padding: 0;
  overflow: visible;
  appearance: none;
  border: 0;
  outline: 0;
  background: transparent;
  transition: all 0.3s ease;
  &:hover,
  &:focus {
    ${TooltipBox}, &::before {
      opacity: 1;
      visibility: visible;
    }
    ${DefaultButton} {
      color: white;
      background: ${colors.waterBlue};
      box-shadow: 0 0 0 2px ${colors.waterBlue} inset;
    }
    ${IconButton} {
      color: ${colors.niceBlue};
    }
  }
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    z-index: 1;
    transform: translate(-16px, -5px);
    width: 0;
    height: 0;
    opacity: 0;
    visibility: hidden;
    border: 8px solid transparent;
    ${props => css`
      border-top-color: ${props.triangleColor};
      border-right-color: ${props.triangleColor};
    `}
    transition: all 0.3s ease;
  }
`;

type State = {
  positionRight: number,
  triangleColor: string
};

type Props = {
  children: React.Node,
  button?: React.Node,
  width?: number | string | Array<number | string>,
  component?: React.ElementType,
  onClick?: Function
};

export default class Tooltip extends React.PureComponent<Props, State> {
  state = {
    positionRight: 0,
    triangleColor: colors.skyBlue
  };

  buttonRef = React.createRef<Element>();

  resizeHandler = () => {
    if (this.buttonRef.current) {
      const rect = this.buttonRef.current.getBoundingClientRect();
      const overflowWidth = 300 - rect.width;
      let positionRight = 0;
      if (rect.left - overflowWidth < 10) {
        positionRight = rect.left - overflowWidth - 10;
      } else {
        positionRight = 0;
      }
      const gradient = {
        a: hexToDecimalArray(colors.niceBlue),
        b: hexToDecimalArray(colors.skyBlue)
      };
      const triangleColorArray = gradient.a.map(
        (g, index) =>
          gradient.a[index] +
          (1 + positionRight / 300) * (gradient.b[index] - gradient.a[index])
      );
      const triangleColor = decimalArrayToHex(triangleColorArray);
      this.setState({ positionRight, triangleColor });
    }
  };

  componentDidMount() {
    if (window) {
      window.addEventListener('resize', this.resizeHandler);
      this.resizeHandler();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  render() {
    return (
      <Button
        ref={this.buttonRef}
        triangleColor={this.state.triangleColor}
        type={
          !this.props.component || this.props.component === 'button'
            ? 'button'
            : undefined
        }
        component={this.props.component || 'button'}
        tabIndex="0"
        aria-label={getText('Show tooltip')}
        onClick={this.props.onClick}
      >
        <TooltipBox right={this.state.positionRight} width={this.props.width}>
          {this.props.children}
        </TooltipBox>
        {this.props.button || <DefaultButton />}
      </Button>
    );
  }
}
